import { useEffect, useState } from 'react';
import { Clock3, GitCompareArrows, Pencil, Check, X, Bookmark } from 'lucide-react';
import { organizationApi } from './api';
import type { OrganizationVersion } from './model';

export const versionDate = (value: string) =>
  new Intl.DateTimeFormat(undefined, { dateStyle: 'medium', timeStyle: 'short' }).format(
    new Date(value)
  );

export default function HistoryPanel({
  companyId,
  versions,
  currentId,
  viewingId,
  canEdit,
  onView,
  onCompare,
  onUpdated,
  error,
  loadingMore,
  hasMore,
  onMore,
}: {
  companyId: string;
  versions: OrganizationVersion[];
  currentId: string | null;
  viewingId?: string;
  canEdit: boolean;
  onView: (version: OrganizationVersion) => void;
  onCompare: (version: OrganizationVersion) => void;
  onUpdated: () => void;
  error?: string;
  loadingMore?: boolean;
  hasMore?: boolean;
  onMore?: () => void;
}) {
  const [editing, setEditing] = useState<string | null>(null);
  const [label, setLabel] = useState('');
  const [note, setNote] = useState('');
  const [saving, setSaving] = useState(false);
  const [failure, setFailure] = useState('');
  useEffect(() => {
    setEditing(null);
    setFailure('');
  }, [companyId, canEdit]);
  const save = async (id: string) => {
    if (!canEdit || saving) return;
    setSaving(true);
    setFailure('');
    try {
      await organizationApi.labelVersion(companyId, id, label.trim() || null, note.trim() || null);
      setEditing(null);
      onUpdated();
    } catch (err) {
      setFailure(err instanceof Error ? err.message : 'Could not name this version.');
    } finally {
      setSaving(false);
    }
  };
  return (
    <div className="org-history-body">
      <div className="org-outline-intro">
        <span className="org-eyebrow">STRUCTURE OVER TIME</span>
        <p>
          Each saved structural change is recorded. Name a version to mark an important milestone.
        </p>
      </div>
      {(error || failure) && (
        <p className="org-error" role="alert">
          {failure || error}
          <button type="button" className="org-text-button" onClick={onUpdated}>
            Retry history
          </button>
        </p>
      )}
      {!versions.length && !error && (
        <div className="org-empty">
          <Clock3 size={24} />
          <p>Your history starts here</p>
          <small>
            The first baseline appears when versioning is activated. Earlier changes cannot be
            reconstructed.
          </small>
        </div>
      )}
      <ol className="org-version-list">
        {versions.map((version) => (
          <li
            key={version.id}
            className={`org-version ${viewingId === version.id ? 'is-selected' : ''}`}
          >
            <div className="org-version-meta">
              <span className="org-version-number">v{version.revision}</span>
              {version.id === currentId && <span className="org-pill">Current</span>}
              {version.label && <Bookmark size={13} className="org-milestone-mark" />}
              {canEdit && (
                <button
                  type="button"
                  className="org-icon-button"
                  aria-label={`Name version ${version.revision}`}
                  onClick={() => {
                    setEditing(version.id);
                    setLabel(version.label || '');
                    setNote(version.note || '');
                    setFailure('');
                  }}
                >
                  <Pencil size={13} />
                </button>
              )}
            </div>
            {canEdit && editing === version.id ? (
              <form
                className="org-version-edit"
                onSubmit={(event) => {
                  event.preventDefault();
                  void save(version.id);
                }}
              >
                <label className="org-field">
                  <span>Milestone name</span>
                  <input
                    value={label}
                    maxLength={160}
                    autoFocus
                    onChange={(event) => setLabel(event.target.value)}
                    placeholder="e.g. October restructure"
                  />
                </label>
                <label className="org-field">
                  <span>Note</span>
                  <textarea
                    value={note}
                    maxLength={2000}
                    onChange={(event) => setNote(event.target.value)}
                    rows={2}
                  />
                </label>
                <div className="org-row">
                  <button type="submit" className="org-button org-button-primary" disabled={saving}>
                    <Check size={13} />
                    {saving ? 'Saving…' : 'Save name'}
                  </button>
                  <button
                    type="button"
                    className="org-icon-button"
                    aria-label="Cancel version naming"
                    disabled={saving}
                    onClick={() => setEditing(null)}
                  >
                    <X size={14} />
                  </button>
                </div>
              </form>
            ) : (
              <>
                <button type="button" className="org-version-view" onClick={() => onView(version)}>
                  <strong>{version.label || version.summary}</strong>
                  <span>{versionDate(version.createdAt)}</span>
                </button>
                <small>
                  {version.actorName || 'System'} · {version.summary}
                </small>
                {version.note && <p className="org-version-note">{version.note}</p>}
                <div className="org-version-actions">
                  <button type="button" className="org-text-button" onClick={() => onView(version)}>
                    View structure
                  </button>
                  <button
                    type="button"
                    className="org-text-button"
                    onClick={() => onCompare(version)}
                  >
                    <GitCompareArrows size={13} />
                    Compare
                  </button>
                </div>
              </>
            )}
          </li>
        ))}
      </ol>
      {hasMore && (
        <button
          type="button"
          className="org-button org-button-secondary org-load-more"
          disabled={loadingMore}
          onClick={onMore}
        >
          {loadingMore ? 'Loading…' : 'Load earlier versions'}
        </button>
      )}
      <p className="org-history-footnote">
        Versions record the structure when changes were saved. Account permissions and invitations
        stay in the current workspace.
      </p>
    </div>
  );
}
